import * as React from "react";
import { useDialog } from "@shared-ui/dialog-context";
import { observer } from "mobx-react";
import { UitkCard, UitkCardLink, UitkCardContentSection } from "uitk-react-cards";
import { CampaignByIdQuery, CampaignRecommendationsWithOffersQuery } from "../../../../../../__generated__/api";
import Offers = CampaignRecommendationsWithOffersQuery.Offers;
import { locKeys } from "components/shared/Merchandising/Inventory/l10n";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkPriceLockup } from "uitk-react-price-lockup";
import { withStores } from "stores";
import { InfoDialog } from "../../Common/InfoDialog/InfoDialog";
import { MabTrackEvent, withMabTracker } from "src/components/utility/analytics/MabTracker";
import { UitkLayoutPosition } from "uitk-react-layout-position";
import { UitkSpacing, UitkSpacingHr } from "uitk-react-spacing";
import { LazyLoadedImage } from "components/utility/LazyLoadedImage/LazyLoadedImage";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { Action } from "components/utility/analytics/FlexAnalyticsUtils";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { UitkBadge, UitkDealMemberBadge, UitkVipBadge } from "uitk-react-badge";
import { UitkIcon, UitkIconSize } from "uitk-react-icons";
import { UitkMark, UitkMarkSize } from "uitk-react-mark";
import { UitkLayoutGrid } from "uitk-react-layout-grid";
import { ExtendedContextStore } from "../../../../../typings/flexFramework/FlexDefinitions";
import HotelOfferItemInlineFragment = CampaignByIdQuery.HotelOfferItemInlineFragment;
import { UitkHeading, UitkText } from "uitk-react-text";
import { UitkLayoutFlex } from "uitk-react-layout-flex";

const MarkBrandMap = {
  expedia: "mod_exp",
  lastminute: undefined,
  orbitz: "mod_orb",
  travelocity: "mod_tvly",
  wotif: "mod_wotif",
  cheaptickets: "mod_ctx",
  ebookers: "mod_ebook",
};

const defaultUndefinedMark = "lastminute";

export interface HotelInventoryCardProps {
  offer: Offers;
  cardIndex: number;
  campaignId: string;
  moduleName: string;
  border?: boolean;
  context?: ExtendedContextStore;
  mabTrack?: (x: MabTrackEvent) => null;
}

export const HotelInventoryCard = withMabTracker(
  ({ offer, cardIndex, campaignId, moduleName, border, mabTrack }: HotelInventoryCardProps) => {
    if (offer.__typename !== "HotelOfferItem") {
      return null;
    }

    const cardClicked = () => {
      if (!mabTrack) return;

      const mabTrackData: MabTrackEvent = {
        moduleName: "geo_hotels_1",
        eventTarget: "geo_hotels_1",
        eventType: "homepageClick.nonPath",
        rfrr: `MerchCampaign.card-${campaignId}-${cardIndex}`,
      };
      mabTrack(mabTrackData);
    };

    return (
      <UitkCard className="HotelInventoryCard" border={border}>
        <UitkFigure ratio={UitkFigureAspectRatioType.R21_9}>
          <ImageBadge offer={offer} />
          <LazyLoadedImage src={offer.image.url} alt={offer.image.description} />
        </UitkFigure>
        <UitkCardContentSection className="HotelInventoryCardSection">
          <UitkSpacing padding={{ blockend: "four" }}>
            <UitkLayoutPosition type="relative">
              <div>
                <UitkSpacing padding={{ blockend: "half" }}>
                  <UitkHeading overflow="truncate" tag="h3" size={5}>
                    {offer.name}
                  </UitkHeading>
                </UitkSpacing>
                <UitkText size={300}>{offer.location}</UitkText>
                {offer.guestReviewRating && (
                  <UitkLayoutPosition type="absolute" position={{ bottom: "minus-two" }} cloneElement>
                    <div className="reviewRating">
                      <UitkLayoutFlex>
                        <UitkSpacing padding={{ inlineend: "one" }}>
                          <UitkText weight="bold" size={300}>
                            {offer.guestReviewRating}
                          </UitkText>
                        </UitkSpacing>
                        <UitkText size={300} className="pwa-theme--grey-700">
                          {offer.guestReviewCount}
                        </UitkText>
                      </UitkLayoutFlex>
                    </div>
                  </UitkLayoutPosition>
                )}
              </div>
            </UitkLayoutPosition>
          </UitkSpacing>
        </UitkCardContentSection>
        <UitkLayoutPosition type="relative">
          <UitkCardContentSection className="HotelInventoryCardSection">
            <Separator offer={offer} />
            {offer.freeCancellationMessage && (
              <UitkLayoutPosition type="absolute" position={{ top: "six" }} cloneElement>
                <UitkText size={300} weight="bold" theme="positive">
                  {offer?.freeCancellationMessage}
                </UitkText>
              </UitkLayoutPosition>
            )}
            <UitkLayoutGrid alignItems="end" columns={2} space="two">
              <>
                <UitkText align="left" size={200}>
                  {offer?.dates}
                </UitkText>
                <div className="uitk-type-right">
                  <PriceComponent
                    offer={offer}
                    cardIndex={cardIndex}
                    campaignId={campaignId}
                    moduleName={moduleName}
                    cardClicked={cardClicked}
                  />
                </div>
              </>
            </UitkLayoutGrid>
          </UitkCardContentSection>
        </UitkLayoutPosition>
        <UitkCardLink>
          {offer.lodgingOffer && (
            <TrackedLink
              moduleName={moduleName}
              action={Action.CLICK}
              href={offer.lodgingOffer?.actionLink.href}
              rfrr={`card-${campaignId}-${cardIndex}`}
              onClick={cardClicked}
            >
              {offer.lodgingOffer?.actionLink.text}
            </TrackedLink>
          )}
        </UitkCardLink>
      </UitkCard>
    );
  }
);

interface MarkIconProps {
  context: ExtendedContextStore;
}

type OfferProps = Pick<HotelInventoryCardProps, "offer">;

const MarkIcon = withStores("context")(
  observer(({ context }: MarkIconProps) => {
    const markName = MarkBrandMap[context.site?.brand ?? defaultUndefinedMark];
    if (markName) {
      return <UitkMark name={markName} size={UitkMarkSize.SMALL} />;
    }
    return <UitkIcon name="mod" size={UitkIconSize.SMALL} />;
  })
);

const ImageBadge: React.FC<OfferProps> = ({ offer }) => {
  const separatorOffer = offer as HotelOfferItemInlineFragment;
  if (separatorOffer?.mod?.modTagText) {
    return (
      <UitkLayoutPosition type="absolute" position={{ top: "three", left: "three", zindex: "layer2" }} cloneElement>
        <UitkDealMemberBadge text={separatorOffer?.mod?.modTagText}>
          <MarkIcon />
        </UitkDealMemberBadge>
      </UitkLayoutPosition>
    );
  }

  if (separatorOffer?.vipMessage && !separatorOffer.mod) {
    return (
      <UitkSpacing margin={{ blockstart: "three", inlinestart: "three" }}>
        <UitkVipBadge className="hotelBadge" text={separatorOffer.vipMessage} />
      </UitkSpacing>
    );
  }
  return null;
};

const SeperatorBadge = ({ modText, badgeText }: { modText?: string | null; badgeText?: string }) => {
  if (modText) {
    return <UitkDealMemberBadge text={badgeText} />;
  }
  return <UitkBadge text={badgeText} />;
};

const Separator: React.FC<OfferProps> = ({ offer }) => {
  const separatorOffer = offer as HotelOfferItemInlineFragment;
  const badgeText = separatorOffer.discountBadge?.text;
  const badgeClass = badgeText ? "separatorLineWidth" : "";

  if (separatorOffer?.mod?.modAvailabilityText) {
    return (
      <>
        <div className={badgeClass}>
          <UitkSpacingHr blockMargin="one" />
        </div>
        <UitkLayoutPosition type="relative" position={{ top: "minus-four" }} cloneElement>
          <div className="uitk-type-right uitk-type-200">
            <UitkDealMemberBadge text={separatorOffer?.mod?.modAvailabilityText} />
          </div>
        </UitkLayoutPosition>
      </>
    );
  }
  if (badgeText) {
    return (
      <>
        <div className={badgeClass}>
          <UitkSpacingHr blockMargin="one" />
        </div>
        <UitkLayoutPosition type="relative" position={{ top: "minus-four" }} cloneElement>
          <div className="uitk-type-right">
            <SeperatorBadge modText={separatorOffer.mod?.modTagText} badgeText={badgeText} />
          </div>
        </UitkLayoutPosition>
      </>
    );
  }
  return <UitkSpacingHr blockMargin="four" />;
};

interface PriceComponentProps {
  offer: Offers;
  cardIndex: number;
  campaignId: string;
  moduleName: string;
  cardClicked: () => void;
}

const PriceComponent = observer(({ offer, cardIndex, campaignId, moduleName, cardClicked }: PriceComponentProps) => {
  const offering = offer as HotelOfferItemInlineFragment;
  const { formatText } = useLocalization();
  const [, dialogActions, PriceDialogComponent] = useDialog(`disclaimer-info-${campaignId}-${cardIndex}`);

  if (offering?.mod?.modSignInText) {
    return (
      <TrackedLink
        moduleName={moduleName}
        action={Action.CLICK}
        rfrr={`card-${campaignId}-${cardIndex}-MOD-SignUp`}
        onClick={cardClicked}
        href={offering?.mod?.signInUrl?.relativePath}
      >
        <UitkText size={300} weight="bold" align="right">
          {offering?.mod?.modSignInText}
        </UitkText>
      </TrackedLink>
    );
  }
  return (
    <>
      <UitkPriceLockup
        lockupPrice={offering.lodgingOffer?.price?.value}
        priceSubtextFirst={offering.lodgingOffer?.price?.qualifierText}
        a11yPrice={offering.lodgingOffer?.price?.allyText || ""}
        a11yStrikeThroughDialogTrigger={formatText(locKeys.priceDisclaimerLabel)}
        strikeThroughPrice={offering?.lodgingOffer?.strikeThroughPrice?.value}
        strikeThroughTrigger={
          <button className="disclaimerInfoIcon" onClick={dialogActions.openDialog} type="button">
            <UitkIcon
              name="info_outline"
              size={UitkIconSize.SMALL}
              id={`disclaimer-info-${campaignId}-${cardIndex}`}
              title={formatText("merchCampaign.disclaimerIcon.title")}
            />
          </button>
        }
      />
      <PriceDialogComponent>
        <InfoDialog
          infoText={offering?.lodgingOffer?.price.disclaimerText}
          closeDialog={dialogActions.closeDialog}
          buttonText={formatText("DestinationsByAffinity.priceDialogCloseButton")}
        />
      </PriceDialogComponent>
    </>
  );
});
